import { CellProps, ColumnDefinition, NodeValues } from "components/TreeState"
import { ChangedPlanRecord, PlanRecord, PlanRecordType } from "./types"
import {
  DualNumericCell,
  ReadonlyDualNumericCell,
} from "./cells/DualNumericCell"
import { GrossCostCell, PreciseGrossCostCell } from "./cells/GrossCostCell"
import {
  MediaField,
  MediaFieldFormat,
  MediaFieldType,
  MediaType,
  SubmediaType,
} from "routes/Base/Admin/GlobalMediaTypes/types"
import { NumericCell, PreciseNumericCell } from "./cells/NumericCell"
import { PreciseReadonlyCell, ReadonlyCell } from "./cells/ReadonlyCell"
import { PreciseVariableCost, VariableCost } from "./cells/VariableCost"
import { queryCache, useQuery } from "react-query"
import { useAtomValue, useUpdateAtom } from "jotai/utils"

import { BillingTypeCell } from "./cells/BillingTypeCell"
import { BoViewType } from "models/bookingOrder"
import { ClientAgencyTarget } from "models/targets"
import { CommisionCell } from "./cells/CommisionCell"
import { Commission } from "models/commissions"
import { CopyCell } from "./cells/CopyCell"
import { CopyQualityCell } from "./cells/CopyQualityCell"
import { CurrencyCell } from "./cells/CurrencyCell"
import { DateCell } from "./cells/DateCell"
import { DaysOfWeekCell } from "./cells/DaysOfWeekCell"
import { DigitalCalculatorCell } from "./cells/DigitalCalculatorCell"
import { ExcludedHoursCell } from "./cells/ExcludedHoursCell"
import { FeeCell } from "./cells/FeeCell"
import { FieldsKey } from "./components/AutoCompute/types"
import { HourIntervalCell } from "./cells/HourIntervalCell"
import { ListCell } from "./cells/ListCell"
import { MediaPlan } from "models/mediaPlan"
import { MediaPreference } from "api/media-types"
import { MetricCell } from "./cells/MetricCell"
import { NegotiationTypeCell } from "./cells/NegotiationTypeCell"
import { NumericWithUnitCell } from "./cells/NumericWithUnitCell"
import { PassthroughCell } from "./cells/PassthroughCell"
import { ProgramCell } from "./cells/ProgramCell"
import { RateCardCost } from "./cells/RateCardCost"
import { SpotsPlanCell } from "./cells/SpotsPlanCell"
import { TargetCell } from "./cells/TargetCell"
import { TaxesCell } from "./cells/TaxesCell"
import { TextCell } from "./cells/TextCell"
import { Vendor } from "./cells/Vendor"
import { VendorProduct } from "./cells/VendorProduct"
import { atom } from "jotai"
import { fetchPlanTargets } from "api/plans"
import { stringifyFormulaSet } from "./components/AutoCompute/formulas"
import { useLocationQuery } from "hooks/useLocationQuery"
import { useParams } from "react-router-dom"
import { lastDayOfMonth } from "date-fns"

export const DEFAULT_SEMANTICS = [
  "budget",
  "start",
  "end",
  "vendor",
  "vendorProduct",
]

const DIGITAL_CALCULATOR_SEMANTICS = [
  "buyingModel",
  "grossCost",
  "unitCost",
  "mainMetric",
  "additionalMetric",
  "benchmarkRatio",
]

const BO_DUAL_EDIT_SEMANTICS = ["grossCost", "unitCost"]

export type ConfigurationType = {
  enabledMediaSemantics: string[]
  preferences: Record<string, MediaPreference[]>
  updatedAt?: number
}

export const HIGH_PRECISION_SEMANTICS = [
  "unitCost",
  "grpA",
  "grpB",
  "cpmA",
  "cpmB",
  "grossCost",
  "netCost",
  "cppA",
  "cppB",
]

export function isSemanticPrecise(semantic?: string) {
  return HIGH_PRECISION_SEMANTICS.includes(semantic ?? "")
}

export function getCellPerType({
  type,
  semantic,
  isDigital,
  isBooking,
  nonTransparent,
  viewType,
}: {
  type: MediaFieldType
  semantic?: string
  isDigital?: boolean
  isBooking?: boolean
  nonTransparent?: boolean
  viewType?: BoViewType
}): React.FunctionComponent<CellProps> {
  let cell: React.FunctionComponent<CellProps>
  switch (type) {
    case "Date":
      cell = DateCell
      break
    case "List":
      cell = ListCell
      break
    case "Readonly":
    case "Calculated":
      cell = isSemanticPrecise(semantic) ? PreciseReadonlyCell : ReadonlyCell
      break
    case "SpotsPlan":
      cell = SpotsPlanCell
      break
    case "VariableCost":
      cell = isSemanticPrecise(semantic) ? PreciseVariableCost : VariableCost
      break
    case "Commission":
      cell = CommisionCell
      break
    case "Numeric":
      cell = isSemanticPrecise(semantic) ? PreciseNumericCell : NumericCell
      break
    case "HourInterval":
      cell = HourIntervalCell
      break
    case "Fees":
      cell = FeeCell
      break
    case "NumericWithUnit":
      cell = NumericWithUnitCell
      break
    case "BillingType":
      cell = BillingTypeCell
      break
    case "Taxes":
      cell = TaxesCell
      break
    default:
      cell = TextCell
      break
  }

  semantic === "budget" && (cell = CurrencyCell)
  semantic === "vendor" && (cell = Vendor)
  semantic === "vendorProduct" && (cell = VendorProduct)
  semantic === "program" && (cell = ProgramCell)
  semantic === "rateCardCost" && (cell = RateCardCost)
  semantic === "daysOfWeek" && (cell = DaysOfWeekCell)
  semantic === "grossCost" &&
    (cell = isSemanticPrecise(semantic) ? PreciseGrossCostCell : GrossCostCell)
  semantic === "excludedHours" && (cell = ExcludedHoursCell)
  semantic === "passthroughCosts" && type === "Fees" && (cell = PassthroughCell)
  semantic === "mainMetric" && (cell = MetricCell)
  semantic === "additionalMetric" && (cell = MetricCell)
  semantic === "benchmarkRatio" && (cell = MetricCell)
  semantic === "targetA" && (cell = TargetCell)
  semantic === "targetB" && (cell = TargetCell)
  semantic === "copy" && (cell = CopyCell)
  semantic === "copyQuality" && (cell = CopyQualityCell)
  semantic === "negotiationType" && (cell = NegotiationTypeCell)

  /*
    In case of a non transparent BO, and if the semantic
    is one of those that the buyer can also change the
    planning values, then the cell renderer must be
    the Dual Numeric Cell
  */
  if (
    isBooking &&
    !!nonTransparent &&
    !!semantic &&
    BO_DUAL_EDIT_SEMANTICS.includes(semantic)
  ) {
    cell =
      viewType === "INTERCOMPANY" ? ReadonlyDualNumericCell : DualNumericCell
  }

  /*
    In case of digital media type, and if the semantic is one
    that must be changed by the Digital Calculator, then the
    cell renderer must be the Digital Calculator Cell
  */
  if (
    !!semantic &&
    !!isDigital &&
    DIGITAL_CALCULATOR_SEMANTICS.includes(semantic)
  ) {
    cell = DigitalCalculatorCell
  }

  return cell
}

export function getDualFields(isBooking?: boolean) {
  let primary = "planningMediaFields",
    secondary = "buyingMediaFields"
  if (isBooking) {
    primary = "buyingMediaFields"
    secondary = "planningMediaFields"
  }

  return { primary, secondary }
}

export function getExtraColumns(
  mediaTypes?: MediaType[],
  options?: {
    booking?: boolean
    nonTransparent?: boolean
    viewType?: "CLIENT_AGENCY" | "INTERCOMPANY"
    onSave?: (
      options?:
        | {
            transparent?: boolean | undefined
            syncUpdate?: boolean | undefined
          }
        | undefined,
    ) => Promise<boolean>
  },
) {
  const extraColumns: Record<string, ColumnDefinition[]> = {}
  const { primary: primaryKey, secondary: secondaryKey } = getDualFields(
    options?.booking,
  )

  mediaTypes?.forEach((mt) => {
    extraColumns[mt.id] = []
    const isDigital = mt.semanticCode === "digital"

    mt.mediaFields.forEach((mdf) => {
      const column: ColumnDefinition = {
        id: mdf.id,
        groupName: mt.name,
        Header: mdf.name,
        dataKey: `${primaryKey}.${mdf.id}`,
        primaryKey,
        secondaryKey,
        semantic: mdf.semanticCode || mdf.name,
        options: mdf.options?.map((o) => ({ value: o, label: o })),
        Cell: getCellPerType({
          type: mdf.type,
          semantic: mdf.semanticCode,
          isDigital,
          isBooking: options?.booking,
          nonTransparent: options?.nonTransparent,
          viewType: options?.viewType,
        }),
        width: 200,
        resizable: true,
        format: mdf.format,
        editType: mdf.type,
        viewType: options?.viewType,
        onSave: options?.onSave,
        infoTooltipTitle: getColumnTooltip(mdf, mt),
      }

      // only add those that are valid for the current scope
      let visible = true

      if (!!mdf.semanticScopes) {
        visible = mdf.semanticScopes?.includes("PLANNING")
        if (options?.booking) {
          visible =
            options?.viewType === "INTERCOMPANY"
              ? mdf.semanticScopes?.includes("INTERCOMPANY")
              : mdf.semanticScopes?.includes("BUYING")
        }
      }

      visible && extraColumns[mt.id].push(column)
    })
  })

  return extraColumns
}

function getColumnTooltip(mediaField: MediaField, mediaType: MediaType) {
  const defaultInfoTooltipTitle =
    mediaField?.formulaSet &&
    `${mediaField.name} = ${stringifyFormulaSet(
      mediaField.formulaSet,
      mediaType.mediaFields,
    )}`
  const grossCostTooltip = mediaField?.formulaSet && (
    <span>
      {defaultInfoTooltipTitle}
      <br />
      <br />
      WARNING: If the Negotiation Type is CPP, the formula is as follows:
      <br />
      {defaultInfoTooltipTitle} * GRP B
    </span>
  )

  return mediaField.semanticCode === "grossCost"
    ? grossCostTooltip
    : defaultInfoTooltipTitle
}

export function applyColumnVisibility(
  column: ColumnDefinition,
  configuration: ConfigurationType,
) {
  let isVisible = true
  if (configuration.preferences && !!configuration.enabledMediaSemantics) {
    configuration.enabledMediaSemantics.forEach((em) => {
      const pref = configuration.preferences[em]?.find(
        (pr) => pr.mediaFieldSemanticCode === column.semantic,
      )
      if (pref) isVisible = pref.visible
    })
  }

  return isVisible
}

export function applyColumnsOrder(
  columns: ColumnDefinition[],
  configuration: ConfigurationType,
) {
  const mediaPrefs =
    !!configuration.preferences && !!configuration.enabledMediaSemantics
      ? configuration.enabledMediaSemantics
          ?.map((em) => configuration.preferences[em])
          .flat()
      : []

  return columns.sort((a, b) => {
    let orderOfA =
      mediaPrefs.find((pr) => pr?.mediaFieldSemanticCode === a.semantic)
        ?.order || 0
    if (orderOfA === -1) orderOfA = columns.length
    let orderOfB =
      mediaPrefs.find((pr) => pr?.mediaFieldSemanticCode === b.semantic)
        ?.order || 0
    if (orderOfB === -1) orderOfB = columns.length

    return orderOfA - orderOfB
  })
}

export function applyColumnsResize(
  columns: ColumnDefinition[],
  configuration: ConfigurationType,
) {
  const mediaPrefs =
    !!configuration.preferences && !!configuration.enabledMediaSemantics
      ? configuration.enabledMediaSemantics
          ?.map((em) => configuration.preferences[em])
          .flat()
      : []

  return columns.map((c) => {
    if (c.id === "name") return c

    const widthPref = mediaPrefs.find(
      (mp) => mp?.mediaFieldSemanticCode === c.semantic,
    )?.width
    c.width = widthPref || c.width

    return c
  })
}

export function getMediaGroups(
  state: Record<string, NodeValues<ChangedPlanRecord>>,
  mediaTypes?: MediaType[],
) {
  const groups = new Set<MediaType & { key: string }>()
  const allKeys = Object.keys(state)

  allKeys.forEach((key) => {
    const v = state[key]
    // const isNotDeleted = v?.action !== "DELETE"
    const isNotPackaged = !v?.packaged
    const isMediaType = v?.type === "media_type_plan"
    const mediaType = mediaTypes?.find((mt) => mt.id === v.mediaId)

    // isNotDeleted &&
    isNotPackaged &&
      isMediaType &&
      !!v?.mediaId &&
      !!v?.mediaName &&
      !!mediaType &&
      groups.add({ ...mediaType, key })
  })

  return [...groups]
}

export function getSubmediaGroups(
  state: Record<string, NodeValues<ChangedPlanRecord>>,
  submediaTypes?: SubmediaType[],
) {
  const groups = new Set<SubmediaType & { key: string }>()
  const allKeys = Object.keys(state)

  allKeys.forEach((key) => {
    const v = state[key]
    const isSubmediaType = v?.type === "media_subtype_plan"
    const submediaType = submediaTypes?.find((smt) => smt.id === v.mediaId)

    isSubmediaType &&
      !!v?.mediaId &&
      !!v?.mediaName &&
      !!submediaType &&
      groups.add({ ...submediaType, key })
  })

  return [...groups]
}

export function normalizePlanErrors(errors: any) {
  const _errors = errors as Record<string, any>

  Object.keys(_errors).forEach((ek) => {
    const err = _errors[ek]
    !!err["mediaId"] && (err.name = err["mediaId"])
  })

  return _errors
}

export function getTypeRelatedFieldFormats(
  type: MediaFieldType,
): MediaFieldFormat[] {
  switch (type) {
    case "Numeric":
    case "Calculated":
      return ["MONETARY", "PERCENTAGE", "NUMERIC"]
    case "Readonly":
    case "VariableCost":
    case "Fees":
    case "Commission":
      return ["MONETARY"]
    case "NumericWithUnit":
      return ["NUMERIC"]
    default:
      return []
  }
}

export function usePlanCachedData(ignoreVersion: boolean) {
  const sp = useLocationQuery()
  const version = ignoreVersion ? null : sp.get("version")
  const { id: planId } = useParams<{ id: string }>()
  const planData = queryCache.getQueryData<MediaPlan>(["plan", planId, version])
  return planData
}

export function sanitizeLineItem(
  item: ChangedPlanRecord,
  mediaTypes?: MediaType[],
  fieldKey: FieldsKey = "planningMediaFields",
) {
  const shouldSanitize = item.type === "line_item" || item.type === "package"
  if (!mediaTypes || !shouldSanitize) return item

  let fields = item[fieldKey]
  if (!fields) return item
  // for all commission fields replace `amount` with `percentage`
  const mediaType = mediaTypes.find((mt) => mt.id === item.ancestorMediaId)
  const { agencyCommissions: acId, mediaCommissions: mcId } =
    mediaType?.defaultFieldIdBySemantic || {}
  const commissionFieldIds = Object.keys(fields).filter((fk) =>
    [acId, mcId].includes(fk),
  )

  if (!commissionFieldIds?.length) return item

  let sanitizedFields: Record<string, any> = {}
  commissionFieldIds?.forEach((cmfk) => {
    if (!!fields) {
      const cf = fields[cmfk] as { commission: Commission }
      if (!!cf) {
        const { amount, type, ...rest } = cf?.commission ?? {}
        if (!!cf?.commission)
          sanitizedFields[cmfk] = {
            commission: { percentage: amount, ...rest },
          }
      }
    }
  })

  fields = { ...fields, ...sanitizedFields }

  return { ...item, [fieldKey]: fields }
}

const rankingsModal = atom<{
  mode?: "APPLY" | "TRANSLATE"
  sourceId?: string
  semantic?: string
}>({})

export function useRankingsModal() {
  const rankings = useAtomValue(rankingsModal)
  const { mode, sourceId, semantic } = rankings
  const updateRankingsModal = useUpdateAtom(rankingsModal)
  const onRankingsClose = () => updateRankingsModal({})

  return {
    mode,
    sourceId,
    semantic,
    updateRankingsModal,
    onRankingsClose,
  }
}

const viewType = atom<"CLIENT_AGENCY" | "INTERCOMPANY" | undefined>(undefined)

export function useViewType() {
  const viewTypeValue = useAtomValue(viewType)
  const updateViewType = useUpdateAtom(viewType)

  return {
    viewTypeValue,
    updateViewType,
  }
}

/*
  readonly rules per semantic
*/

const readOnlyPerSemantic = (
  predicates?: Record<string, boolean>,
): Record<string, Array<PlanRecordType | "packaged">> => ({
  unitPrice: ["package", "media_subtype_plan", "media_type_plan"],
  agencyCommissions: ["packaged", "media_subtype_plan", "media_type_plan"],
  mediaCommissions: ["packaged", "media_subtype_plan", "media_type_plan"],
  budget: predicates?.isBooking
    ? [
        "packaged",
        "package",
        "line_item",
        "media_subtype_plan",
        "media_type_plan",
      ]
    : ["packaged", "package", "line_item"],
  start: predicates?.isBooking
    ? ["media_subtype_plan", "media_subtype_plan"]
    : [],
  end: predicates?.isBooking
    ? ["media_subtype_plan", "media_subtype_plan"]
    : [],
  excludedHours: ["package", "media_subtype_plan", "media_type_plan"],
  benchmarkRatio: ["package", "media_subtype_plan", "media_type_plan"],
  discount: ["package", "media_subtype_plan", "media_type_plan"],
  fees: ["media_type_plan", "media_subtype_plan", "packaged"],
  taxes: ["media_type_plan", "media_subtype_plan", "packaged"],
  netCost: ["media_type_plan", "media_subtype_plan", "packaged"],
  mainMetric: ["media_type_plan", "media_subtype_plan"],
  additionalMetric: ["media_type_plan", "media_subtype_plan"],
  passthroughCosts: ["package", "media_subtype_plan", "media_type_plan"],
  // depended on copy
  rateCardCost: predicates?.isCopySet
    ? [
        "package",
        "media_type_plan",
        "media_subtype_plan",
        "packaged",
        "line_item",
      ]
    : ["package", "media_type_plan", "media_subtype_plan"],
  duration: predicates?.isCopySet
    ? [
        "packaged",
        "package",
        "line_item",
        "media_subtype_plan",
        "media_type_plan",
      ]
    : ["media_subtype_plan", "media_type_plan"],
  language: predicates?.isCopySet
    ? [
        "packaged",
        "package",
        "line_item",
        "media_subtype_plan",
        "media_type_plan",
      ]
    : ["media_subtype_plan", "media_type_plan"],
  copyQuality: predicates?.isCopySet
    ? [
        "packaged",
        "package",
        "line_item",
        "media_subtype_plan",
        "media_type_plan",
      ]
    : ["media_subtype_plan", "media_type_plan"],
})

export function isSemanticReadonly(
  type: PlanRecordType,
  primaryKey: FieldsKey,
  semantic?: string,
  packaged?: boolean,
  defaultFieldIds?: Record<string, string>,
  mediaFields?: PlanRecord["planningMediaFields"],
) {
  if (!semantic) return false
  // guards
  const isBooking = primaryKey === "buyingMediaFields"
  const copyFieldId = defaultFieldIds?.copy
  const isCopySet = !!copyFieldId ? !!mediaFields?.[copyFieldId] : false

  const rulePerSemantic = readOnlyPerSemantic({
    isBooking,
    isCopySet,
  })[semantic]

  // for packaged line items check all
  if (packaged && rulePerSemantic?.includes("packaged")) return true

  // check for specific semantics
  if (semantic === "unitPrice") return rulePerSemantic?.includes(type)
  if (semantic === "budget") return rulePerSemantic?.includes(type)
  if (semantic === "discount") return rulePerSemantic?.includes(type)
  if (semantic === "benchmarkRatio") return rulePerSemantic?.includes(type)
  if (semantic === "excludedHours") return rulePerSemantic?.includes(type)

  // check for copy dependants
  if (
    ["duration", "language", "copyQuality", "rateCardCost", "taxes"].includes(
      semantic,
    )
  )
    return rulePerSemantic?.includes(type)
}

export function useAvailableTargets() {
  const { id: planId } = useParams<{ id: string }>()
  const cached = queryCache.getQueryData<Partial<ClientAgencyTarget>[]>([
    "client-targets",
    planId,
  ])

  const { data: availableTargets } = useQuery(
    ["client-targets", planId],
    fetchPlanTargets,
    {
      enabled: !cached,
      initialData: cached,
      refetchOnMount: !cached,
    },
  )
  return availableTargets
}

const convertToDate = (dateSource: string | Date | number) => {
  if (dateSource instanceof Date) {
    return dateSource
  } else {
    return new Date(dateSource)
  }
}

export const getMaxDateSameMonth = (
  minDate: string | Date | number,
  maxDate: string | Date,
) => {
  const convertedMinDate = convertToDate(minDate)
  const convertedMaxDate = convertToDate(maxDate)
  const lastDayMinDate = lastDayOfMonth(convertedMinDate)

  return lastDayMinDate > convertedMaxDate ? convertedMaxDate : lastDayMinDate
}
